import * as yup from "yup";
import { validator } from "@/utils/validator";

// 上限は会社作成画面のバリデーションに合わせる: 氏名 ≤ 104、1号残期間は 年 ≤ 4 / 月 ≤ 11。
export const LEARNER_FULL_NAME_MAX_LENGTH = 104;
export const RESIDENCE_PERIOD_MAX_YEARS = 4;
export const RESIDENCE_PERIOD_MAX_MONTHS = 11;
export const RESIDENCE_PERIOD_FIELD_MAX_LENGTH = 8;
export const RESIDENCE_PERIOD_FORMAT_ERROR_MESSAGE = "一号残期間は「○年○か月」「○年」または「○か月」の形式で入力してください。";
export const RESIDENCE_PERIOD_RANGE_ERROR_MESSAGE = `一号残期間は${RESIDENCE_PERIOD_MAX_YEARS}年${RESIDENCE_PERIOD_MAX_MONTHS}か月以内で入力してください。`;

const RESIDENCE_PERIOD_PATTERNS: { regex: RegExp; getYears: (m: RegExpMatchArray) => number; getMonths: (m: RegExpMatchArray) => number }[] = [
  { regex: /^(\d{1,2})年(\d{1,2})か月$/, getYears: (m) => Number(m[1]), getMonths: (m) => Number(m[2]) },
  { regex: /^(\d{1,2})年$/, getYears: (m) => Number(m[1]), getMonths: () => 0 },
  { regex: /^(\d{1,2})か月$/, getYears: () => 0, getMonths: (m) => Number(m[1]) },
];

type ResidencePeriodParseResult =
  | { kind: "empty" }
  | { kind: "invalid-format" }
  | { kind: "out-of-range"; months: number }
  | { kind: "ok"; months: number };

export const parseResidencePeriod = (raw: string): ResidencePeriodParseResult => {
  const trimmed = raw.trim();
  if (trimmed === "") return { kind: "empty" };
  for (const { regex, getYears, getMonths } of RESIDENCE_PERIOD_PATTERNS) {
    const match = trimmed.match(regex);
    if (!match) continue;
    const years = getYears(match);
    const months = getMonths(match);
    const total = years * 12 + months;
    if (years > RESIDENCE_PERIOD_MAX_YEARS || months > RESIDENCE_PERIOD_MAX_MONTHS) {
      return { kind: "out-of-range", months: total };
    }
    return { kind: "ok", months: total };
  }
  return { kind: "invalid-format" };
};

// API パラメータ用: 空 → null、フォーマット/範囲不正 → undefined、正常 → 月数。
export const parseResidencePeriodToMonths = (raw: string): number | null | undefined => {
  const result = parseResidencePeriod(raw);
  if (result.kind === "empty") return null;
  if (result.kind === "ok") return result.months;
  return undefined;
};

function residencePeriodTest(this: yup.TestContext, value: string | null | undefined) {
  if (value == null || value === "") return true;
  const result = parseResidencePeriod(value);
  if (result.kind === "ok") return true;
  if (result.kind === "out-of-range") {
    return this.createError({ message: RESIDENCE_PERIOD_RANGE_ERROR_MESSAGE });
  }
  return this.createError({ message: RESIDENCE_PERIOD_FORMAT_ERROR_MESSAGE });
}

const RESIDENCE_PERIOD_TO_GTE_FROM_MESSAGE = "1号残期間の終了月は開始月以降を入力してください";

// 「○年○か月」レンジ: To が From より小さい場合エラー (両方フォーマット正常時のみ)。
function residencePeriodToGteFromTest(this: yup.TestContext, value: string | null | undefined) {
  if (value == null || value === "") return true;
  const from = (this.parent as { residencePeriodMonthsFrom?: string | null }).residencePeriodMonthsFrom;
  if (from == null || from === "") return true;
  const fromMonths = parseResidencePeriodToMonths(from);
  const toMonths = parseResidencePeriodToMonths(value);
  if (typeof fromMonths !== "number" || typeof toMonths !== "number") return true;
  return toMonths >= fromMonths;
}

export const learnerReportFilterSchema = validator.object({
  fullName: validator
    .string()
    .label("氏名")
    .emptyToNull()
    .max(LEARNER_FULL_NAME_MAX_LENGTH)
    .nullable()
    .notRequired(),

  nationalityIds: validator.array().of(validator.string()).label("国籍").notRequired(),
  genders: validator.array().of(validator.string()).label("性別").notRequired(),
  prefectures: validator.array().of(validator.string()).label("居住地").notRequired(),

  residencePeriodMonthsFrom: validator
    .string()
    .label("一号残期間")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .nullable()
    .notRequired()
    .test("residence-period-format", RESIDENCE_PERIOD_FORMAT_ERROR_MESSAGE, residencePeriodTest),

  residencePeriodMonthsTo: validator
    .string()
    .label("一号残期間")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .nullable()
    .notRequired()
    .test("residence-period-format", RESIDENCE_PERIOD_FORMAT_ERROR_MESSAGE, residencePeriodTest)
    .test("residence-period-to-gte-from", RESIDENCE_PERIOD_TO_GTE_FROM_MESSAGE, residencePeriodToGteFromTest),
});
